<template>
  <div class="cut-video-form scrollable">
    <div class="cut-video-form__hint ">
      <span
        v-if="
          modeSelect === $options.MODES.DRAG || mode === $options.MODES.PREVIEW
        "
        class="fontStyle"
      >
        Select Crop Intervals
      </span>
      <span
        v-else-if="modeSelect === $options.MODES.REMOVE"
        class="slider-note fontStyle"
      >
        Select Interval To Remove
      </span>
      <span
        v-else-if="modeSelect === $options.MODES.SELECT_PREVIEW"
        class="slider-note fontStyle"
      >
        Select Preview Range
      </span>
    </div>
    <div
      class="zoomable-ranges-slider"
      ref="timelineContainer"
      @mousedown="startDraggingTimeline($event)"
      @mouseup="stopDraggingTimeline($event)"
      @mouseleave="stopDraggingTimeline($event)"
      @mousemove="draggingTimeline($event)"
    >
      <scale-labels
        :segments="scaleLabels"
        :style="{
          width: zoomedWidth
        }"
      />
      <div
        class="thumbnails-container containerImg "
        :style="{ width: timeLineWidth }"
      >
        <div
          v-for="img in defImg"
          :key="img"
          class="thumbnails-container__item"
          :style="{
            width: (100 / thumbnailsInViewport) + '%',
            height: 'auto'
          }"
        >
          <thumbnail-image
            :src="img"
            :fallback="$options.fallback"
            class="thumb-img"
            alt="thumbnail"
          />
        </div>
      </div>
      <multiple-ranges-slider
        v-model="localRanges"
        :max-value="duration"
        :range-hoverable="areRangesHoverable"
        :disabled="disabled"
        :style="{
          width: zoomedWidth
        }"
        @rangeClicked="handleRangeClicked($event.index)"
      />
    </div>
    <div class="row cut-video-form__zoom zoom-container">
      <cut-zoom-slider
        v-model="zoom"
        :min="roundingToIntegers"
        class="zoom-container__content"
      />
    </div>
    <div class="row cut-video-form__main">
      <!-- Dragging mode buttons (add/remove/crop) -->
      <template v-if="modeSelect === $options.MODES.DRAG">
        <div class="button-container">
          <el-button
            class="button-container__btn fontStyle"
            type="primary"
            :title="addRangeButtonTitle"
            :disabled="widestEmptyRange[0] < 0 || disabled"
            @click="addRange"
          >
            Add Crop Markers
          </el-button>
        </div>
        <div class="button-container">
          <el-button
            class="button-container__btn fontStyle"
            type="danger"
            title="Remove Interval"
            :disabled="disabled || localRanges.length < 2"
            @click="enableRemoveMode"
          >
            Remove Crop Markers
          </el-button>
        </div>
        <div class="button-container hideButton">
          <el-button
            class="button-container__btn fontStyle"
            type="success"
            title="Preview"
            @click="startPreview"
          >
            Preview
          </el-button>
        </div>
        <el-select
          v-show="false"
          v-model="selected"
          class="dropdown_select_cutform1"
          :placeholder="selected"
        >
          <el-option
            v-for="item in transitiionTypeOptions"
            :key="item.label"
            :label="item.label"
            :value="item.value"
          >
            {{ item.label }}
          </el-option>
        </el-select>
      </template>
      <!-- Remove mode buttons (cancel) -->
      <template
        v-else-if="
          modeSelect === $options.MODES.REMOVE ||
            modeSelect === $options.MODES.SELECT_PREVIEW
        "
      >
        <div class="button-container">
          <el-button
            class="button-container__btn fontStyle"
            type="primary"
            @click="enableDragMode"
          >
            Cancel
          </el-button>
        </div>
      </template>
      <template v-else-if="modeSelect === $options.MODES.PREVIEW">
        <div class="button-container">
          <el-button
            class="button-container__btn fontStyle"
            type="primary"
            @click="enableDragMode"
          >
            Stop
          </el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ThumbnailImage from "@/components/timeline/thumbnails/ThumbnailImage.vue";
import fallbackImage from "@/assets/images/empty.jpg";
import { mapState, mapActions } from "vuex";
import MultipleRangesSlider from "@/components/sliders/MultipleRangesSlider.vue";
import ScaleLabels from "@/components/timeline/scale/ScaleLabels.vue";
import CutZoomSlider from "@/components/media/editing/CutZoomSlider.vue";
import { getAPI, token } from "@/api/params.js";
import { roundToMilliseconds } from "@/utils/time.js";

import { FADE_IN, FADE_OUT, DISCRETE } from "./Transition_types";

/** time constants */
const HOUR = 3600;
const MINUTE = 60;
const SECOND = 1;

const THUMBNAILS_IN_VIEWPORT = 6;

import {
  MODES_DRAG,
  MODES_REMOVE,
  MODES_PREVIEW,
  MODES_SELECT_PREVIEW
} from "./cut_modes";

// @group Editing
// Form to cut ranges from video clip
export default {
  name: "CutVideoForm",
  components: {
    MultipleRangesSlider,
    ScaleLabels,
    CutZoomSlider,
    ThumbnailImage
  },
  model: {
    prop: "ranges",
    event: "updateRanges"
  },
  props: {
    selectedSourceVideoId: {
      type: Number,
      default: 0
    },
    /** cutting ranges */
    ranges: {
      type: Array,
      required: true
    },
    /** original clip duration */
    duration: {
      type: Number,
      default: 10
    },
    /** are all functions disabled */
    disabled: {
      type: Boolean,
      default: false
    },
    /** inverse crop transition type (NOT IMPLEMENETD YET) */
    transitionType: {
      type: String,
      default: DISCRETE
    },
    videoInfo: {
      type: Object,
      required: true
    },
    thumbnailSize: {
      type: Object,
      default() {
        return {
          w: (100 / THUMBNAILS_IN_VIEWPORT) + "%", // probably deprecated
          h: 100
        };
      }
    },
    /** form mode (drag/remove/preview/select_preview) */
    mode: {
      type: String,
      default: MODES_DRAG,
      validator(val) {
        return [
          MODES_DRAG,
          MODES_REMOVE,
          MODES_PREVIEW,
          MODES_SELECT_PREVIEW
        ].includes(val);
      }
    },
    modeSelect: {
      type: String,
      required: true
    }
  },
  fallback: fallbackImage,
  /** modes */
  MODES: {
    /** dragging mode: change ranges borders */
    DRAG: MODES_DRAG,
    /** remove mode: select range to remove */
    REMOVE: MODES_REMOVE,
    /**  preview mode: view the selected preview interval */
    PREVIEW: MODES_PREVIEW,
    /** select preview mode: select range to preview */
    SELECT_PREVIEW: MODES_SELECT_PREVIEW
  },
  /** possible variants of time difference between two time labels */
  availableTimesteps: [
    12 * HOUR,
    6 * HOUR,
    4 * HOUR,
    3 * HOUR,
    2 * HOUR,
    HOUR,
    30 * MINUTE,
    20 * MINUTE,
    10 * MINUTE,
    5 * MINUTE,
    2 * MINUTE,
    MINUTE,
    30 * SECOND,
    20 * SECOND,
    10 * SECOND,
    5 * SECOND,
    3 * SECOND,
    2 * SECOND,
    1 * SECOND
  ],
  THUMBNAILS_IN_VIEWPORT: THUMBNAILS_IN_VIEWPORT,
  data() {
    const thumbnailsInViewport = 6;
    const timelineContainer = document.querySelector('.thumbnails-container')
    if(timelineContainer) {
      thumbnailsInViewport = timelineContainer.getBoundingClientRect().width / 220;
    }
    return {
      thumbnailsInViewport: thumbnailsInViewport,
      updateThumbnailsController: null,
      defImg: [],
      selectVideoId: this.videoInfo.id,
      selectVideoDuration: this.videoInfo.metadata.duration,
      minZoomValue: 500 / this.videoInfo.metadata.duration,
      /*
      selectEvenSpacing: this.videoInfo.metadata.duration / THUMBNAILS_IN_VIEWPORT,
      frameRate:
        (this.videoInfo.metadata.duration / this.videoInfo.metadata.duration) / THUMBNAILS_IN_VIEWPORT,
      */
      selected: this.transitionType,
      transitiionTypeOptions: [
        {
          value: FADE_IN,
          label: "Fade in"
        },
        {
          value: FADE_OUT,
          label: "Fade out"
        },
        {
          value: DISCRETE,
          label: "Discrete"
        }
      ],
      /** zoom params */
      zoom: 1,
      maxVisibleLabels: 6,

      dragOrigin: null
    };
  },
  computed: {
    ...mapState({
      /** stream info */
      thumbs: state => state.services.thumbs
    }),
    selectEvenSpacing() {
      return this.videoInfo.metadata.duration / this.thumbnailsInViewport;
    },
    frameRate() {
      return (this.videoInfo.metadata.duration / this.videoInfo.metadata.duration) / this.thumbnailsInViewport;
    },
    // @vuese
    // Get/set property to pass v-model prop "ranges" to v-bind directive
    localRanges: {
      get() {
        return this.ranges;
      },
      set(val) {
        this.$emit("updateRanges", val);
      }
    },
    timeLineWidth() {
      return (100 / this.thumbnailsInViewport * this.duration) / (this.visibleDuration / this.thumbnailsInViewport) + "%";
    },
    // @vuese
    // Are ranges can be highlighted by hover
    areRangesHoverable() {
      return [
        this.$options.MODES.REMOVE,
        this.$options.MODES.SELECT_PREVIEW,
        this.$options.MODES.PREVIEW,
        this.$options.MODES.DRAG
      ].includes(this.mode);
    },
    // @vuese
    // Maximum range length
    maxLength() {
      return roundToMilliseconds(this.duration);
    },
    // @vuese
    // Initial thumbnail timestamps for first load (without zoom)
    initialThumbnailTimestamps() {
      const timestamps = [];
      for (let i = 0; i < this.thumbnailsInViewport; i++) {
        timestamps.push(i * this.selectEvenSpacing);
      }
      return timestamps; // .map(e => Math.round(e));
    },
    timeLineChange() {
      const timeokrugChange = this.visibleDuration;
      const durationChoiceVideo = this.duration;
      const numberOfPoints = timeokrugChange / this.thumbnailsInViewport;
      const numberOfSections = Math.floor(durationChoiceVideo / numberOfPoints);
      const cutTime = [];
      for (let i = 0; i < numberOfSections; i++) {
        cutTime.push(i);
      }
      const fullCutTime = cutTime.map(e => (e * numberOfPoints));
      return fullCutTime;
    },
    roundingToIntegers() {
      return Number((Math.ceil(this.minZoomValue) * 0.01).toFixed(2));
    },
    // @vuese
    // List of breaks beetwen ranges (includes video start & end points])
    emptyRanges() {
      const emptyRanges = [];

      /** if there is no one range in slider, then all space is empty range */
      if (this.localRanges.length === 0) {
        return [[0, this.maxLength]];
      }

      /** set first point as slider start */
      let lastEnd = 0;

      for (const [start, end] of this.localRanges) {
        const emptySize = start - lastEnd;

        if (emptySize > 0) {
          /** add new break, if it's not between adjacent ranges */
          emptyRanges.push([lastEnd, start]);
        }

        lastEnd = end;
      }

      /** add new break between the last range's end point and end of the slider */
      if (lastEnd < this.maxLength) {
        emptyRanges.push([lastEnd, this.maxLength]);
      }

      return emptyRanges;
    },
    // @vuese
    // The widest break between two ranges. Equals to [-1, -1] when there is no any break
    widestEmptyRange() {
      let maxEmpty = {
        start: -1,
        end: -1,
        size: 0
      };

      for (const [start, end] of this.emptyRanges) {
        const emptySize = end - start;

        /** update the widest break */
        if (emptySize > maxEmpty.size) {
          maxEmpty = {
            start,
            end,
            size: emptySize
          };
        }
      }

      return [maxEmpty.start, maxEmpty.end];
    },
    // @vuese
    // Tooltip title for Add Range button
    addRangeButtonTitle() {
      return this.widestEmptyRange[0] < 0
        ? "No space for new interval"
        : "Add new interval";
    },
    // @vuese
    // Width of intervals slider
    zoomedWidth() {
      return 100 / this.zoom + "%";
    },
    // @vuese
    // Visible slider duration in seconds
    visibleDuration() {
      return this.duration * this.zoom;
    },
    // @vuese
    // Time difference between two labels
    scaleLabelsStep() {
      const steps = this.$options.availableTimesteps;
      let minStep = steps[0];

      /** find the minimum valiable time step */
      for (const step of steps) {
        if (this.visibleDuration / step <= this.maxVisibleLabels) {
          minStep = step;
        }
      }
      return minStep;
    },
    // @vuese
    // Time labels list with segment sizes
    scaleLabels() {
      const duration = this.duration;
      const step = Math.max(this.scaleLabelsStep, 1);
      const segments = [];

      for (let time = 0; time < duration; time += step) {
        const hours = Math.floor(time / HOUR);
        const mins = Math.floor((time % HOUR) / MINUTE);
        const secs = time % MINUTE;

        const timeParts = duration > HOUR ? [hours, mins, secs] : [mins, secs];
        const label = timeParts.map(n => n.toString().padStart(2, "0")).join(":");

        const nextTime = Math.min(time + step, duration);

        segments.push({
          label,
          size: nextTime - time
        });
      }

      return segments;
    }
  },
  watch: {
    modeSelect() {
      this.$emit("selectMode", this.modeSelect);
    },
    selectedSourceVideoId() {
      this.selectVideoId = this.selectedSourceVideoId;
      this.updateThumbnails();
    },
    mode() {
      this.$emit("mode", this.mode);
    },
    // @vuese
    // Reset remove mode when lefts only one range
    localRanges() {
      if (
        this.localRanges.length < 2 &&
        this.mode === this.$options.MODES.REMOVE
      ) {
        this.enableDragMode();
      }
    },
    selected() {
      return this.$emit("update:transitionType", this.selected);
    },
    timeLineChange() {
      this.updateThumbnails();
    },
    zoom() {
      this.keepTimelineCenter();
    }
  },
  mounted() {
    this.updateNumberOfThumbnails();
    window.addEventListener('resize', this.updateNumberOfThumbnails);
    fetch(
      `${getAPI()}/video/${
        this.selectVideoId
      }/generate_thumbnails?token=${token}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(this.initialThumbnailTimestamps)
      }
    )
      .then(response => response.json())
      .then(resp => {
        const nameUrlVideo = resp.data;
        this.defImg = this.initialThumbnailTimestamps.map(
          e => `${this.videoInfo.storageUrl}/videos/${nameUrlVideo}/${Math.floor(e * 1000)}.jpg`
        );
      });
    // alert(1);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateNumberOfThumbnails);
  },
  methods: {
    // @vuese
    // Update number of thumbnails based on container width
    updateNumberOfThumbnails() {
      const timelineContainer = document.querySelector('.zoomable-ranges-slider');
      if (timelineContainer) {
        this.thumbnailsInViewport = Math.ceil(timelineContainer.getBoundingClientRect().width / 220);
      }
    },
    updateThumbnails() {
      if (this.updateThumbnailsController !== null) {
        this.updateThumbnailsController.abort();
        // this.updateThumbnailsController = new AbortController();
      }
      this.updateThumbnailsController = new AbortController();
      const signal = this.updateThumbnailsController.signal;
      fetch(
        `${getAPI()}/video/${
          this.selectVideoId
        }/generate_thumbnails?token=${token}`,
        {
          method: "POST",
          signal: signal,
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(this.timeLineChange)
        }
      )
        .then(response => response.json())
        .then(resp => {
          this.updateThumbnailsController = null;
          if (resp.success == false) {
            return;
          }
          const nameUrlVideo = resp.data;
          this.defImg = this.timeLineChange.map(
            e => `${this.videoInfo.storageUrl}/videos/${nameUrlVideo}/${Math.floor(e * 1000)}.jpg`
          );
        });
    },
    // @vuese
    // Enable dragging mode
    enableDragMode() {
      this.$emit("changeMode", MODES_DRAG);
      this.$emit("stopPreview");
    },
    // @vuese
    // Enable remove mode
    enableRemoveMode() {
      this.$emit("changeMode", MODES_REMOVE);
    },
    // @vuese
    // Handle click on the range
    handleRangeClicked(index) {
      if (
        this.modeSelect != this.$options.MODES.REMOVE &&
        this.modeSelect != this.$options.MODES.SELECT_PREVIEW
      ) {
        this.$emit("changeMode", MODES_PREVIEW);
        // change mutating prop
        // this.modeSelect = MODES_PREVIEW;
      }
      switch (this.modeSelect) {
        case this.$options.MODES.REMOVE:
          this.removeRange(index);
          break;
        case this.$options.MODES.SELECT_PREVIEW:
          this.playRange(index);
          break;
        case this.$options.MODES.DRAG:
        case this.$options.MODES.PREVIEW:
          // this.$emit("stopPreview");
          this.playRange(index);
          break;
      }
    },
    // @vuese
    // Add new range instead of the longest break between two ranges
    addRange() {
      const range = this.widestEmptyRange;

      /** we're finding the range to put new one before it */
      for (let i = 0; i < this.localRanges.length; i++) {
        const start = this.localRanges[i][0];
        if (start >= range[1]) {
          this.localRanges.splice(i, 0, range);
          return;
        }
      }

      /** if there is no one range to put new one before, then we put it in the end of the slider */
      this.localRanges = [...this.localRanges, range];
    },
    // @vuese
    // Remove existing range
    // @arg index - range index
    removeRange(index) {
      this.localRanges.splice(index, 1);
    },
    // @vuese
    // Play preview of selected range
    // @arg index - index of preview range
    playRange(index = 0) {
      this.$emit("preview", this.localRanges[index]);
    },
    // @vuese
    // Handle Preview-button click
    startPreview() {
      if (this.localRanges.length > 1) {
        this.$emit("changeMode", MODES_SELECT_PREVIEW);
      } else {
        this.playRange();
      }
    },

    keepTimelineCenter() {
      const container = this.$refs.timelineContainer;
      if (!container || !container.scrollWidth) {
        return;
      }

      const { scrollLeft, clientWidth, scrollWidth } = container;
      const centerRatio = (scrollLeft + clientWidth / 2) / scrollWidth;

      this.$nextTick(() => {
        const nextContainer = this.$refs.timelineContainer;
        if (!nextContainer || !nextContainer.scrollWidth) {
          return;
        }

        const targetCenter = centerRatio * nextContainer.scrollWidth;
        const nextScrollLeft = targetCenter - nextContainer.clientWidth / 2;
        nextContainer.scrollLeft = Math.max(0, nextScrollLeft);
      });
    },

    startDraggingTimeline(event) {
      let canDrag = false;
      let target = event.target;
      while(target !== event.currentTarget) {
        if(target.classList.contains('thumbnails-container')) {
          canDrag = true;
          break;
        } else {
          target = target.parentNode;
        }
      }
      if(canDrag) {
        event.currentTarget.style.cursor = 'grab';
        this.dragOrigin = {
          x: event.clientX,
          y: event.clientY,
          scroll: event.currentTarget.scrollLeft
        }
      }
    },
    stopDraggingTimeline(event) {
      if(this.dragOrigin !== null) {
        event.currentTarget.style.cursor = '';
        this.dragOrigin = null;
      }
    },
    draggingTimeline(event) {
      if(this.dragOrigin !== null) {
        event.currentTarget.scrollLeft = this.dragOrigin.scroll + (this.dragOrigin.x - event.clientX);
      }
    }
  }
};
</script>

<style>
:root {
  /** colors */
  --dark-grey: #3d3d3d;
  --grey: #5b5b5b;
  --light-grey: #878787;

  /** layers for modals */
  --modal-dropdown-z: 3200;
  --modal-z: 3150;
  --modal-backdrop-z: 3100;

  /** layer for notifications panel */
  --notifs-z: 3000;

  /** navigation bar layers */
  --navbar-dropdown-z: 2050;
  --navbar-z: 2000;

  --video-thumb-overlay-z: 50;

  /** player layers */
  --player-z: 50;
  --player-controls-z: 100;

  /** areas */
  --edit-area-z: 50;

  /** timeine layers */
  --timeline-popup-z: 300;
  --timeline-cursor-z: 120;
  --timeline-break-z: 20;

  /** left handle wrapper should to have more z-index to prevent ovelaying from right handle wrapper */
  --timeline-left-handle-z: 16;
  --timeline-right-handle-z: 15;
  --timeline-slider-z: 15;
  --timeline-faded-z: 20;
  --timeline-label-z: 10;
  --timeline-duration-z: 30;
  --timeline-button-z: 170;
}

#app {
  font-family: Poppins, Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  background-color: var(--light-grey);
  height: 100vh;
}

#nav {
  padding: 30px;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}

/* Scrollbars */
/* width */
.scrollable ::-webkit-scrollbar {
  width: 15px;
}

/* .scroll-invisible::-webkit-scrollbar {
  display: none;
} */

.scroll-invisible {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}

/* Track */
.scrollable ::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

/* Handle */
.scrollable ::-webkit-scrollbar-thumb {
  background: var(--dark-grey);
  border-radius: 10px;
}

/* Handle on hover */
.scrollable ::-webkit-scrollbar-thumb:hover {
  background: #2d2d2d;
  border-radius: 10px;
}

/** modal */
.modal-select-dropdown {
  z-index: var(--modal-dropdown-z) !important;
}
</style>

<style scoped lang="scss">
/* Scrollbars */
/* width */
.scrollable ::-webkit-scrollbar {
  width: 15px;
}

.fontStyle {
  // font-family: sans-serif;
}

/* .scroll-invisible::-webkit-scrollbar {
  display: none;
} */

.scroll-invisible {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}

/* Track */
.scrollable ::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

/* Handle */
.scrollable ::-webkit-scrollbar-thumb {
  background: var(--dark-grey);
  border-radius: 10px;
}

/* Handle on hover */
.scrollable ::-webkit-scrollbar-thumb:hover {
  background: #2d2d2d;
  border-radius: 10px;
}

/** modal */
.modal-select-dropdown {
  z-index: var(--modal-dropdown-z) !important;
}

.cut-video-form {
  &__hint {
    margin-bottom: 1em;
  }

  &__zoom {
    margin-bottom: auto;
  }
}
.button-container {
  margin-right: 0.5em;
  margin-left: 0.5em;
  margin-bottom: 0.5em;

  &__btn {
    width: 13em;
  }
}
.hideButton {
  display: none;
}

.thumbnails-container {
  white-space: nowrap;
  overflow: hidden;
  margin: 0;
  padding: 0;
  user-select: none;
  &__item {
    display: inline-block;
  }
}

.thumb-img {
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
}

.slider-note {
  color: yellow;
}

/** buttons row */
.zoomable-ranges-slider {
  padding: 0em 0.5em 1em; // padding for range pointers
  margin-bottom: 0.5em;

  overflow-y: hidden;
  overflow-x: scroll;

  &__slider {
    width: 200% !important;
  }
}

.zoom-container {
  &__content {
    width: 400px;
  }
}
</style>

<style scoped>
.dropdown_select_cutform1 input {
  height: 40px;
  border-radius: 4px;
  /* background-color: white; */
  border: 1px solid #fff;
}

.containerImg {
  /* //width: 100%; */
  height: 80px;
  height: 120px;
  display: flex;
}
@media only screen and (max-width: 639px), only screen and (max-height: 740px) {
  .cut-modal__form {
    /* height: 330px; */
    /* width: 370px; */
  }

  .zoom-container__content {
    width: 340px;
  }

  .zoomable-ranges-slider {
    /* width: 350px; */
    height: 170px;
    margin-bottom: 17px;
  }
}
</style>
